/**
 * SuggestionsPage — repointed to the rebuilt `/api` surface (#1151 PR-4).
 *
 * Lists pending purchase suggestions grouped by supplier. The rebuilt
 * backend replaced the archived "batch-select rows → create POs per
 * supplier" flow with a per-suggestion lifecycle:
 *
 *   - Accept  → POST /api/purchase-suggestions/:id/accept { unit_price }
 *               creates a draft PO from that single suggestion.
 *   - Dismiss → POST /api/purchase-suggestions/:id/dismiss
 *               drops the suggestion from the queue.
 *
 * The operator types a `unit_price` per row (required by the accept
 * contract) then accepts. `suggested_qty` / `unit_price` are
 * NUMERIC(12,3)-as-text (ADR-0003): render parses via `parseDecimalText`
 * at the display boundary, and the price ships back as the raw typed
 * string so the backend column receives the exact value with no
 * IEEE-754 re-serialisation jitter.
 *
 * Permission gate is route-level (`INGREDIENTS_READ` for view,
 * `INGREDIENTS_WRITE` for accept / dismiss). See `App.tsx` mount.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageShell,
  EmptyState,
  ErrorState,
  Button,
  Input,
  Loading,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { NavGroupTabs } from '@components/layout/NavGroupTabs';
import { useSuggestions, useAcceptSuggestion, useDismissSuggestion } from '@features/procurement';
import type { PurchaseSuggestion } from '@/services';
import { formatNumber } from '@lib/utils';
import { parseDecimalText, normalizeDecimalText } from '@/services';

export function SuggestionsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const query = useSuggestions({ status: 'pending' });
  const accept = useAcceptSuggestion();
  const dismiss = useDismissSuggestion();

  // Per-row unit-price entry, keyed by suggestion id.
  const [prices, setPrices] = useState<Record<string, string>>({});
  // The set of rows currently mid-mutation. Per-id (not a single id) so a
  // second row's action does not re-enable the first row's in-flight
  // buttons — each row disables independently, closing the double-submit
  // window a shared `busyId` left open.
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const startPending = (id: string) =>
    setPendingIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  const endPending = (id: string) =>
    setPendingIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });

  const suggestions: PurchaseSuggestion[] = query.data?.items ?? [];

  // Group suggestions by supplier. Map preserves insertion order so
  // the supplier blocks render newest-first (matching the list sort).
  const grouped = useMemo(() => {
    const map = new Map<string, PurchaseSuggestion[]>();
    for (const s of suggestions) {
      const bucket = map.get(s.supplier_id);
      if (bucket) {
        bucket.push(s);
      } else {
        map.set(s.supplier_id, [s]);
      }
    }
    return map;
  }, [suggestions]);

  const handleAccept = async (row: PurchaseSuggestion) => {
    const raw = prices[row.id];
    if (raw === undefined || raw === '') {
      toast.error(t('procurement.suggestions.priceRequired'));
      return;
    }
    // Normalize to the ADR-0003 wire contract before send: the number
    // input can emit `.5` / `5.` / `1e3`, all of which the backend
    // decimal regex 400s. `null` (e.g. `1e3`) is a hard reject.
    const unitPrice = normalizeDecimalText(raw);
    if (unitPrice === null || Number(unitPrice) <= 0) {
      toast.error(t('procurement.suggestions.invalidPrice'));
      return;
    }
    startPending(row.id);
    try {
      await accept.mutateAsync({ id: row.id, input: { unit_price: unitPrice } });
      toast.success(t('procurement.suggestions.acceptSuccess'));
      setPrices((prev) => {
        const next = { ...prev };
        delete next[row.id];
        return next;
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      endPending(row.id);
    }
  };

  const handleDismiss = async (row: PurchaseSuggestion) => {
    startPending(row.id);
    try {
      await dismiss.mutateAsync(row.id);
      toast.success(t('procurement.suggestions.dismissSuccess'));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      endPending(row.id);
    }
  };

  return (
    <PageShell
      data-testid="procurement-suggestions-page"
      title={t('procurement.suggestions.title')}
      subtitle={t('procurement.suggestions.subtitle')}
      tabs={<NavGroupTabs group="procurementSuppliers" />}
      actions={
        <Button variant="outline" onClick={() => query.refetch()} data-testid="suggestions-refresh">
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('procurement.common.refresh')}
        </Button>
      }
    >
      {query.isLoading ? (
        <Loading />
      ) : query.isError ? (
        <Card>
          <CardContent>
            <ErrorState message={t('procurement.suggestions.loadError')} />
          </CardContent>
        </Card>
      ) : grouped.size === 0 ? (
        <Card>
          <CardContent>
            <EmptyState title={t('procurement.suggestions.empty')} />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Array.from(grouped.entries()).map(([supplierId, rows]) => (
            <Card key={supplierId} data-testid={`supplier-group-${supplierId}`}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>
                    {t('procurement.suggestions.supplier', {
                      supplier: supplierId,
                    })}
                  </span>
                  <span className="text-sm font-normal text-muted-foreground">
                    {t('procurement.suggestions.lineCount', {
                      count: rows.length,
                    })}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-muted-foreground border-b">
                      <th className="py-2">{t('procurement.suggestions.col.ingredient')}</th>
                      <th className="py-2 text-right">
                        {t('procurement.suggestions.col.suggestedQty')}
                      </th>
                      <th className="py-2">{t('procurement.suggestions.col.unit')}</th>
                      <th className="py-2 text-right">
                        {t('procurement.suggestions.col.unitPrice')}
                      </th>
                      <th className="py-2 text-right">
                        {t('procurement.suggestions.col.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const busy = pendingIds.has(row.id);
                      return (
                        <tr
                          key={row.id}
                          className="border-b last:border-0"
                          data-testid={`suggestion-row-${row.id}`}
                        >
                          <td className="py-2 font-medium">{row.ingredient_id}</td>
                          <td className="py-2 text-right tabular-nums">
                            {formatNumber(
                              parseDecimalText(
                                row.suggested_qty,
                                `SuggestionsPage:${row.id}:suggested_qty`,
                              ),
                              { maximumFractionDigits: 3 },
                            )}
                          </td>
                          <td className="py-2 text-muted-foreground">{row.unit}</td>
                          <td className="py-2 text-right">
                            <Input
                              type="number"
                              min="0"
                              step="0.001"
                              value={prices[row.id] ?? ''}
                              placeholder={t('procurement.suggestions.unitPricePh')}
                              onChange={(e) =>
                                setPrices((prev) => ({
                                  ...prev,
                                  [row.id]: e.target.value,
                                }))
                              }
                              className="w-28 text-right"
                              data-testid={`suggestion-price-${row.id}`}
                            />
                          </td>
                          <td className="py-2">
                            <div className="flex justify-end gap-2">
                              <Button
                                size="sm"
                                disabled={busy}
                                onClick={() => handleAccept(row)}
                                data-testid={`suggestion-accept-${row.id}`}
                              >
                                {t('procurement.suggestions.accept')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={busy}
                                onClick={() => handleDismiss(row)}
                                data-testid={`suggestion-dismiss-${row.id}`}
                              >
                                {t('procurement.suggestions.dismiss')}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="pt-4">
        <Link
          to="/procurement/purchase-orders"
          className="text-sm text-primary hover:underline"
          data-testid="procurement-link-pos"
        >
          {t('procurement.suggestions.viewPOs')}
        </Link>
      </div>
    </PageShell>
  );
}

export default SuggestionsPage;
